import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

const AUTH_EXCLUDED_PATHS = [
  "/auth/login",
  "/auth/register",
  "/auth/password-reset-request",
  "/auth/password-reset-confirm",
  "/auth/refresh",
];

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("vi_token") || null);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const clearAuthState = useCallback(() => {
    localStorage.removeItem("vi_token");
    localStorage.removeItem("vi_refresh");
    setToken(null);
    setUser(null);
  }, []);

  const handleSessionExpired = useCallback(() => {
    const currentPath = `${window.location.pathname}${window.location.search}`;
    const safeReturnTo = currentPath && !currentPath.startsWith("/login") && !currentPath.startsWith("/register") && !currentPath.startsWith("/reset-password") && !currentPath.startsWith("/forgot-password") ? currentPath : "/";
    localStorage.setItem("vi_return_to", safeReturnTo);
    clearAuthState();
    setSessionExpired(true);
    navigate(`/login?session=expired&next=${encodeURIComponent(safeReturnTo)}`, { replace: true });
  }, [clearAuthState, navigate]);

  const apiInstance = useMemo(() => {
    const instance = axios.create({ baseURL: API });
    instance.interceptors.request.use((config) => {
      const t = localStorage.getItem("vi_token");
      if (t) config.headers.Authorization = `Bearer ${t}`;
      return config;
    });
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        const status = error?.response?.status;
        const url = error?.config?.url || "";
        const hasToken = Boolean(localStorage.getItem("vi_token"));
        const isAuthCall = AUTH_EXCLUDED_PATHS.some((path) => url.includes(path));
        if (status === 401 && hasToken && !isAuthCall) {
          handleSessionExpired();
          error.isSessionExpired = true;
        }
        return Promise.reject(error);
      }
    );
    return instance;
  }, [handleSessionExpired]);

  const api = useCallback(() => apiInstance, [apiInstance]);

  const fetchUser = useCallback(async () => {
    const t = localStorage.getItem("vi_token");
    if (!t) { setLoading(false); return; }
    try {
      const res = await api().get("/auth/me");
      setUser(res.data);
    } catch (err) {
      if (!err?.isSessionExpired) clearAuthState();
    } finally {
      setLoading(false);
    }
  }, [api, clearAuthState]);

  useEffect(() => { fetchUser(); }, [fetchUser]);

  useEffect(() => {
    if (!location.pathname.startsWith("/login")) setSessionExpired(false);
  }, [location.pathname]);

  const login = async (email, password) => {
    const res = await api().post("/auth/login", { email, password });
    localStorage.setItem("vi_token", res.data.token);
    localStorage.setItem("vi_refresh", res.data.refresh_token);
    setToken(res.data.token);
    setUser(res.data.user);
    setSessionExpired(false);
    return res.data;
  };

  const register = async (tokenVal, full_name, job_title, password) => {
    const res = await api().post("/auth/register", { token: tokenVal, full_name, job_title, password });
    localStorage.setItem("vi_token", res.data.token);
    localStorage.setItem("vi_refresh", res.data.refresh_token);
    setToken(res.data.token);
    setUser(res.data.user);
    setSessionExpired(false);
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("vi_return_to");
    setSessionExpired(false);
    clearAuthState();
  };

  const dismissSessionExpired = () => setSessionExpired(false);

  const isAdmin = user?.role === "master_admin";
  const isClientAdmin = user?.role === "client_admin";

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, api: api(), isAdmin, isClientAdmin, sessionExpired, dismissSessionExpired }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
}
